import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  BoardDeletePayload,
  BoardListPayload,
  BreakdownValuesPayload,
  ColumnDeletePayload,
  ColumnEditBody,
  ColumnEditPayload,
  CreateSetBody,
  CreateSetPayload,
  CsvConfig,
  CsvInspectPayload,
  CsvResultPayload,
  CsvStagePayload,
  DeleteTargetPayload,
  RollupPayload,
  ScorecardPivotPayload,
  SeriesDeletePayload,
  SeriesHistoryPayload,
  SeriesUpdatePayload,
  TargetListPayload,
  TargetSeriesPayload,
  UpdateSetBody,
  UpdateSetPayload,
  UpdateTargetPayload,
} from '../models/scorecardApi.types'

import { appHttp, unwrapEnvelope } from '@/shared/http'

/**
 * Axios config that routes errors to the caller (both flag forms, sibling parity).
 * Error envelopes (INVALID_TARGET_VALUE, UNSOURCEABLE_LEVEL, EMPTY_PLAN, PARSE_FAILED,
 * 404…) reject as `ApiError` for inline, code-based handling instead of a global toast.
 */
const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

/**
 * Scorecards API client — the feature's only HTTP boundary (contract 10 §4/§6). Every
 * route is workspace-scoped under `/accounts/{account_id}/workspaces/{workspace_id}/scorecards/`
 * and returns the `{ status, data, message }` success envelope; every method unwraps it and
 * returns the DTO (module 03 api-class contract).
 *
 * RBAC is enforced server-side: reads + CSV inspect/preview need `viewer`;
 * create/edit/delete + CSV commit need `editor`. The FE additionally gates edit
 * affordances by role (see `useScorecardRbac`).
 */
export class ScorecardApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private base(accountId: number, workspaceId: number): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/scorecards`
  }

  /* ── Reads (viewer) ─────────────────────────────────────────────────────── */

  /** `GET /` — every target row (List view), pre-derived + two-lens. */
  async listTargets(
    accountId: number,
    workspaceId: number,
    signal?: AbortSignal,
  ): Promise<TargetListPayload> {
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.get(`${this.base(accountId, workspaceId)}/`, config)
    return unwrapEnvelope<TargetListPayload>(response)
  }

  /** `GET /<target_id>/series` — one target's daily curve + summary (Chart modal). */
  async getSeries(
    accountId: number,
    workspaceId: number,
    targetId: number,
    signal?: AbortSignal,
  ): Promise<TargetSeriesPayload> {
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/${targetId}/series/`,
      config,
    )
    return unwrapEnvelope<TargetSeriesPayload>(response)
  }

  /** `GET /sets` — board list (Grid board picker). Sorted -end_date, name; includes empty boards. */
  async listBoards(
    accountId: number,
    workspaceId: number,
    signal?: AbortSignal,
  ): Promise<BoardListPayload> {
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.get(`${this.base(accountId, workspaceId)}/sets/`, config)
    return unwrapEnvelope<BoardListPayload>(response)
  }

  /** `GET /sets/<set_id>/scorecard` — board pivot (Grid view). */
  async getBoard(
    accountId: number,
    workspaceId: number,
    setId: number,
    signal?: AbortSignal,
  ): Promise<ScorecardPivotPayload> {
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/sets/${setId}/scorecard/`,
      config,
    )
    return unwrapEnvelope<ScorecardPivotPayload>(response)
  }

  /** `GET /sets/<set_id>/rollup?group_by=` — aggregated roll-up (Roll-up view). */
  async getRollup(
    accountId: number,
    workspaceId: number,
    setId: number,
    groupBy: string,
    signal?: AbortSignal,
  ): Promise<RollupPayload> {
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS, params: { group_by: groupBy } }
    if (signal) config.signal = signal
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/sets/${setId}/rollup/`,
      config,
    )
    return unwrapEnvelope<RollupPayload>(response)
  }

  /**
   * `GET /breakdown-values?breakdown_type=&data_level=` — axis value picker (Create wizard).
   * Serves the **sub** picker too: pass the sub dimension as `breakdownType`. `dataLevel`
   * keys the per-workspace dropdownable cache (ORP-17836 / ORP-17889).
   */
  async getBreakdownValues(
    accountId: number,
    workspaceId: number,
    breakdownType: string,
    dataLevel?: string | null,
    subBreakdownType?: string | null,
    signal?: AbortSignal,
  ): Promise<BreakdownValuesPayload> {
    const params: Record<string, string> = { breakdown_type: breakdownType }
    if (dataLevel) params.data_level = dataLevel
    if (subBreakdownType) params.sub_breakdown_type = subBreakdownType
    const config: AxiosRequestConfig = {
      ...FEATURE_OWNED_ERRORS,
      params,
    }
    if (signal) config.signal = signal
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/breakdown-values/`,
      config,
    )
    return unwrapEnvelope<BreakdownValuesPayload>(response)
  }

  /** `GET /series/<series_id>/history` — recurring timeline (ORP-17835; empty until then). */
  async getSeriesHistory(
    accountId: number,
    workspaceId: number,
    seriesId: number,
    signal?: AbortSignal,
  ): Promise<SeriesHistoryPayload> {
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.get(
      `${this.base(accountId, workspaceId)}/series/${seriesId}/history/`,
      config,
    )
    return unwrapEnvelope<SeriesHistoryPayload>(response)
  }

  /* ── Writes (editor) ────────────────────────────────────────────────────── */

  /** `POST /sets` — create a board (fan-out). 201 → { scorecard_id, created }. */
  async createSet(
    accountId: number,
    workspaceId: number,
    body: CreateSetBody,
  ): Promise<CreateSetPayload> {
    const response = await this.http.post(
      `${this.base(accountId, workspaceId)}/sets/`,
      body,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<CreateSetPayload>(response)
  }

  /** `PATCH /sets/<set_id>` — update board name, dates, and cells in place. Breakdown is immutable. */
  async updateBoard(
    accountId: number,
    workspaceId: number,
    setId: number,
    body: UpdateSetBody,
  ): Promise<UpdateSetPayload> {
    const response = await this.http.patch(
      `${this.base(accountId, workspaceId)}/sets/${setId}/`,
      body,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<UpdateSetPayload>(response)
  }

  /** `PATCH /<target_id>` — retarget one cell. `target_value` is a dot-only decimal > 0. */
  async updateTarget(
    accountId: number,
    workspaceId: number,
    targetId: number,
    targetValue: string,
  ): Promise<UpdateTargetPayload> {
    const response = await this.http.patch(
      `${this.base(accountId, workspaceId)}/${targetId}/`,
      { target_value: targetValue },
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<UpdateTargetPayload>(response)
  }

  /** `DELETE /<target_id>` — delete one cell (realized facts cascade). */
  async deleteTarget(
    accountId: number,
    workspaceId: number,
    targetId: number,
  ): Promise<DeleteTargetPayload> {
    const response = await this.http.delete(
      `${this.base(accountId, workspaceId)}/${targetId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<DeleteTargetPayload>(response)
  }

  /** `PATCH /sets/<set_id>/columns/<metric>` — bulk-edit a whole metric column. */
  async updateColumn(
    accountId: number,
    workspaceId: number,
    setId: number,
    metricName: string,
    body: ColumnEditBody,
  ): Promise<ColumnEditPayload> {
    const url = `${this.base(accountId, workspaceId)}/sets/${setId}/columns/${encodeURIComponent(metricName)}/`
    const response = await this.http.patch(url, body, FEATURE_OWNED_ERRORS)
    return unwrapEnvelope<ColumnEditPayload>(response)
  }

  /** `DELETE /sets/<set_id>/columns/<metric>` — drop a metric column. */
  async deleteColumn(
    accountId: number,
    workspaceId: number,
    setId: number,
    metricName: string,
  ): Promise<ColumnDeletePayload> {
    const url = `${this.base(accountId, workspaceId)}/sets/${setId}/columns/${encodeURIComponent(metricName)}/`
    const response = await this.http.delete(url, FEATURE_OWNED_ERRORS)
    return unwrapEnvelope<ColumnDeletePayload>(response)
  }

  /** `DELETE /sets/<set_id>` — delete a whole board (targets + realized facts cascade). */
  async deleteBoard(
    accountId: number,
    workspaceId: number,
    setId: number,
  ): Promise<BoardDeletePayload> {
    const response = await this.http.delete(
      `${this.base(accountId, workspaceId)}/sets/${setId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<BoardDeletePayload>(response)
  }

  /** `PATCH /series/<series_id>` — stop (is_active:false) / resume (true) the daily roll-forward. */
  async updateSeries(
    accountId: number,
    workspaceId: number,
    seriesId: number,
    isActive: boolean,
  ): Promise<SeriesUpdatePayload> {
    const response = await this.http.patch(
      `${this.base(accountId, workspaceId)}/series/${seriesId}/`,
      { is_active: isActive },
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<SeriesUpdatePayload>(response)
  }

  /** `DELETE /series/<series_id>` — delete a series (past occurrences survive as standalone boards). */
  async deleteSeries(
    accountId: number,
    workspaceId: number,
    seriesId: number,
  ): Promise<SeriesDeletePayload> {
    const response = await this.http.delete(
      `${this.base(accountId, workspaceId)}/series/${seriesId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope<SeriesDeletePayload>(response)
  }

  /* ── CSV import (multipart) ─────────────────────────────────────────────── */

  /** `POST /csv/inspect` (viewer) — column roles + structure hint. */
  async csvInspect(
    accountId: number,
    workspaceId: number,
    file: File,
    opts: { delimiter?: string; encoding?: string } = {},
    signal?: AbortSignal,
  ): Promise<CsvInspectPayload> {
    const form = new FormData()
    form.append('csv_file', file)
    if (opts.delimiter) form.append('delimiter', opts.delimiter)
    if (opts.encoding) form.append('encoding', opts.encoding)
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.post(
      `${this.base(accountId, workspaceId)}/csv/inspect/`,
      form,
      config,
    )
    return unwrapEnvelope<CsvInspectPayload>(response)
  }

  /**
   * `POST /csv/stage` (viewer) — holds the file for the Orpheus setup chat; never writes. The
   * `file_ref` it answers lives an hour, for this user and workspace only, and rides the next chat
   * turn (ORP-26203).
   */
  async csvStage(
    accountId: number,
    workspaceId: number,
    file: File,
    signal?: AbortSignal,
  ): Promise<CsvStagePayload> {
    const form = new FormData()
    form.append('csv_file', file)
    const config: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) config.signal = signal
    const response = await this.http.post(
      `${this.base(accountId, workspaceId)}/csv/stage/`,
      form,
      config,
    )
    return unwrapEnvelope<CsvStagePayload>(response)
  }

  /** `POST /csv/preview` (viewer) — never writes (committed:false). */
  async csvPreview(
    accountId: number,
    workspaceId: number,
    file: File,
    config: CsvConfig,
    signal?: AbortSignal,
  ): Promise<CsvResultPayload> {
    const requestConfig: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) requestConfig.signal = signal
    const response = await this.http.post(
      `${this.base(accountId, workspaceId)}/csv/preview/`,
      this.csvForm(file, config),
      requestConfig,
    )
    return unwrapEnvelope<CsvResultPayload>(response)
  }

  /** `POST /csv/commit` (editor) — writes atomically (committed:true; 201 when rows written). */
  async csvCommit(
    accountId: number,
    workspaceId: number,
    file: File,
    config: CsvConfig,
    signal?: AbortSignal,
  ): Promise<CsvResultPayload> {
    const requestConfig: AxiosRequestConfig = { ...FEATURE_OWNED_ERRORS }
    if (signal) requestConfig.signal = signal
    const response = await this.http.post(
      `${this.base(accountId, workspaceId)}/csv/commit/`,
      this.csvForm(file, config),
      requestConfig,
    )
    return unwrapEnvelope<CsvResultPayload>(response)
  }

  private csvForm(file: File, config: CsvConfig): FormData {
    const form = new FormData()
    form.append('csv_file', file)
    form.append('config', JSON.stringify(config))
    return form
  }
}

export const scorecardApi = new ScorecardApi()
